import { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

import EnhancedPractitionerDropdown from 'components/EnhancedPractitionerDropdown/EnhancedPractitionerDropdown';
import PractitionerDropdownSection from 'components/PractitionerDropdownSection/PractitionerDropdownSection';
import { IPractitionerOptions } from 'pages/ClinicDetailsPage/BookNowPanel/SingleBooking/SingleBooking';
import { useAppSelector } from 'redux/reduxHooks';
import { getPractitionerOptions } from 'services/APIs/getAllClinicsPractitioners';
import { parseQueryUrl } from 'utils/parseQueryUrl';
import { PRACTITIONER_SEARCH_TITLE } from 'utils/constants/common';

import styles from './PractitionerSelector.module.scss';

export interface PractitionerValue {
  id: string;
  title: string;
}

export const NO_PREFERENCE_PRACTITIONER: PractitionerValue = {
  id: 'all',
  title: PRACTITIONER_SEARCH_TITLE,
};

interface Props {
  clinicId: string;
  serviceId?: string | null;
  isExamAndCleaningService?: boolean;
  value: PractitionerValue;
  dentistValue: PractitionerValue;
  onChange: (practitioner: PractitionerValue) => void;
  onChangeDentist: (dentist: PractitionerValue) => void;
}

const PractitionerSelector = ({
  clinicId,
  serviceId,
  isExamAndCleaningService = false,
  value,
  dentistValue,
  onChange,
  onChangeDentist,
}: Props) => {
  const location = useLocation();

  const { codeUrl, serviceSlugNameUrl } = parseQueryUrl(location);

  const { isDoctorSelectionEnabled } = useAppSelector(
    (state) => state.clinicDetailsSlice.clinicData
  );

  const [options, setOptions] = useState<IPractitionerOptions[]>([]);

  const [dentistOptions, setDentistOptions] = useState<IPractitionerOptions[]>(
    []
  );

  const [isOpen, setIsOpen] = useState(false);

  const isDoctorDisplayedForExamCleaning =
    isDoctorSelectionEnabled && isExamAndCleaningService;

  useEffect(() => {
    const source = axios.CancelToken.source();

    const fetchPractitioners = async () => {
      if (!serviceId) return;

      const practitioners = await getPractitionerOptions({
        clinicId,
        serviceId,
        sortBy: 'specialist',
        cancelToken: source.token,
        code: codeUrl,
        serviceSlugName: serviceSlugNameUrl,
      });

      setOptions(practitioners);
    };

    const fetchDentists = async () => {
      if (!serviceId) return;

      const dentists = await getPractitionerOptions({
        clinicId,
        serviceId,
        sortBy: 'specialist',
        cancelToken: source.token,
        shouldFetchSplitSchedulingDoctors: true,
        code: codeUrl,
        serviceSlugName: serviceSlugNameUrl,
      });

      setDentistOptions(dentists);
    };

    setOptions([]);
    setDentistOptions([]);
    setIsOpen(false);
    fetchPractitioners();

    if (isDoctorDisplayedForExamCleaning) {
      fetchDentists();
    }

    return () => source.cancel();
  }, [
    clinicId,
    codeUrl,
    isDoctorDisplayedForExamCleaning,
    serviceId,
    serviceSlugNameUrl,
  ]);

  const isEnhancedPractitionerDropdownDisplayed =
    isDoctorDisplayedForExamCleaning && dentistOptions.length > 0;

  const handleChangeEnhancedOption = (
    isHygienistTab: boolean,
    practitionerId: string
  ) => {
    const currentValue = isHygienistTab ? value : dentistValue;

    if (currentValue.id === practitionerId) return;

    const availableOptions = isHygienistTab ? options : dentistOptions;

    const selectedOption = availableOptions.find(
      (option) => option.id === practitionerId
    );

    const nextValue: PractitionerValue = selectedOption
      ? { id: selectedOption.id, title: selectedOption.title }
      : NO_PREFERENCE_PRACTITIONER;

    if (isHygienistTab) {
      onChange(nextValue);

      return;
    }

    onChangeDentist(nextValue);
  };

  return (
    <div className={styles['container']}>
      {isEnhancedPractitionerDropdownDisplayed ? (
        <EnhancedPractitionerDropdown
          practitionerOptions={options}
          dentistOptions={dentistOptions}
          currentPractitioner={value}
          currentDentist={dentistValue}
          onChange={handleChangeEnhancedOption}
          isPopperFullWidth
        />
      ) : (
        <PractitionerDropdownSection
          isOpen={isOpen}
          options={options}
          optionValue={value}
          onChangeIsOpen={setIsOpen}
          onChangePractitionerOption={onChange}
          defaultOption={NO_PREFERENCE_PRACTITIONER}
          isPopperFullWidth
        />
      )}
    </div>
  );
};

export default PractitionerSelector;
